<template>
  <div>
    <HeaderBreadcrumbs :items="[
      { label: 'SmartPin', to: { name: 'smartpininsight' } },
      { label: 'Insight', to: { name: 'smartpininsight' } },
      { label: 'Detail Content' },
    ]" title="Insight Detail Content" />

    <div class="flex w-full flex-col bg-white rounded-xl p-4 shadow-sm mb-3">
      <div class="flex items-center justify-between mb-4">
        <h2 class="text-xl mb-0 font-bold text-gray-900 tracking-tight">
          Summary
        </h2>
        <div class="flex items-center gap-3">
          <DateRangePicker v-model="selectedDateRange" :max-days="31" @change="handleDateRangeChange" />
          <div class="w-56">
            <CustomSelected
              class="cursor-pointer"
              :value="selectedLocationOption"
              :options="locationOptions"
              label="name"
              track-by="value"
              :multiple="false"
              :default-text="'All Locations'"
              @input="handleLocationChange"
            />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <MostContentCard
          variant="purchase"
          :total="mostPurchase.total"
          :items="mostPurchase.items"
          :is-loading="isLoadingMostPurchase"
        />
        <MostContentCard
          variant="read"
          :total="mostRead.total"
          :items="mostRead.items"
          :is-loading="isLoadingMostRead"
        />
      </div>
    </div>

    <div class="flex w-full flex-col bg-white rounded-xl p-4 shadow-sm mb-5">
      <div class="flex justify-between items-center mb-4">
        <div class="relative w-96">
          <SearchOutline className="absolute top-1/2 left-3 transform -translate-y-1/2" />
          <input
            v-model="searchQuery"
            type="search"
            placeholder="Search by Content Title"
            class="w-full py-2.5 pl-9 pr-3 text-sm border border-gray-300 rounded-lg bg-gray-50"
            @input="onSearch"
          />
        </div>
        <ExportCsvButton
          :is-empty="!totalRows"
          :is-exporting="isExporting"
          :countdown="exportCountdown"
          :countdown-text="formattedExportCountdown"
          @click="exportCsv"
        />
      </div>

      <DataTable
        :headers="headers"
        :data="tableRows"
        :row-key="['content_title', 'author', ...metricKeys]"
        :currentPage="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :withAction="false"
        :withScrollX="true"
        className="w-full"
      >
        <template #content_title="{ value }">
          <span :title="value || '-'">{{ value || '-' }}</span>
        </template>
        <template #author="{ value }">
          <span :title="value || '-'">{{ value || '-' }}</span>
        </template>
        <template #empty>
          <InsightEmptyState :variant="appliedSearchQuery ? 'search' : 'empty'" />
        </template>
      </DataTable>

      <div class="flex justify-between pt-4 pl-3 pr-3 mt-1 bg-white border-t border-gray-100" v-if="totalRows">
        <div class="font-semibold">
          <span class="font-normal text-gray-500">Showing </span>
          {{ firstRowNumber }}-{{ lastRowNumber }}
          <span class="font-normal text-gray-500">of</span> {{ totalRows }}
        </div>
        <div class="mb-2">
          <t-pagination :disabled="isLoading" :total-items="totalRows" :per-page="perPage" v-model="currentPage"
            @change="onChangePage" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs.vue';
import DateRangePicker from '@/components/form/DateRangePicker.vue';
// Nama tag bukan `Table` supaya tidak dibaca Sonar sebagai elemen <table> HTML (rule: table butuh <th>)
import DataTable from '@/components/form/table.vue';
import CustomSelected from '@/components/form/CustomSelected.vue';
import MostContentCard from '@/components/smart-pin/MostContentCard.vue';
import InsightEmptyState from '@/components/smart-pin/InsightEmptyState.vue';
import ExportCsvButton from '@/components/smart-pin/ExportCsvButton.vue';
import SearchOutline from '@/components/icons/SearchOutline';
import insightFilterMixin, { toQueryDate } from '@/components/smart-pin/mixins/insightFilterMixin';
import exportCooldownMixin from '@/components/smart-pin/mixins/exportCooldownMixin';

export default {
  name: 'SmartPinInsightContent',
  components: {
    HeaderBreadcrumbs,
    DateRangePicker,
    DataTable,
    CustomSelected,
    MostContentCard,
    InsightEmptyState,
    ExportCsvButton,
    SearchOutline,
  },
  mixins: [
    insightFilterMixin,
    exportCooldownMixin('smartpin_insight_content_export_until'),
  ],
  data() {
    return {
      mostPurchase: { total: 0, items: [] },
      mostRead: { total: 0, items: [] },
      isLoadingMostPurchase: false,
      isLoadingMostRead: false,
      contents: [],
      totalRows: 0,
      currentPage: 1,
      perPage: 10,
      isLoading: false,
      searchQuery: '',
      appliedSearchQuery: '',
      searchTimeout: null,
      metricKeys: ['view', 'download', 'reading', 'checkout', 'purchase', 'subscription'],
      headers: [
        { label: 'No', key: 'no' },
        { label: 'Content Title', key: 'content_title' },
        { label: 'Author', key: 'author' },
        { label: 'View PDP', key: 'view' },
        { label: 'Download', key: 'download' },
        { label: 'Reading', key: 'reading' },
        { label: 'Checkout', key: 'checkout' },
        { label: 'Purchase', key: 'purchase' },
        { label: 'Subscription', key: 'subscription' },
      ],
    };
  },
  computed: {
    tableRows() {
      return this.contents.map((row) => {
        const formatted = { ...row };
        this.metricKeys.forEach((key) => {
          formatted[key] = Number(row[key] ?? 0).toLocaleString('id-ID');
        });
        return formatted;
      });
    },
    firstRowNumber() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    lastRowNumber() {
      return Math.min(this.currentPage * this.perPage, this.totalRows);
    },
  },
  mounted() {
    this.fetchFilteredData();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
  },
  methods: {
    ...mapActions('smartpin', [
      'getInsightContents',
      'getInsightMostContent',
      'downloadInsightContentsCsv',
    ]),

    onFilterChange() {
      this.currentPage = 1;
      this.fetchFilteredData();
    },

    onSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchContents();
      }, 500);
    },

    exportCsv() {
      if (!this.totalRows) return;

      const [startDate, endDate] = this.selectedDateRange;
      this.runExport(() => this.downloadInsightContentsCsv({
        ...this.filterPayload,
        query: this.searchQuery.trim(),
        fileName: `insight-content_${toQueryDate(startDate)}_${toQueryDate(endDate)}.csv`,
      }));
    },

    fetchFilteredData() {
      this.fetchMostContent('most_purchase', 'mostPurchase', 'isLoadingMostPurchase');
      this.fetchMostContent('most_read', 'mostRead', 'isLoadingMostRead');
      this.fetchContents();
    },

    async fetchMostContent(type, dataKey, loadingKey) {
      this[loadingKey] = true;

      try {
        this[dataKey] = await this.getInsightMostContent({ ...this.filterPayload, type });
      } catch (error) {
        this[dataKey] = { total: 0, items: [] };
        const label = type === 'most_purchase' ? 'most purchase' : 'most read';
        this.alertTopEnd('error', error?.response?.data?.message || `Failed to load ${label} content`, false);
      } finally {
        this[loadingKey] = false;
      }
    },

    async fetchContents() {
      this.isLoading = true;

      const query = this.searchQuery.trim();

      try {
        const { rows, count } = await this.getInsightContents({
          ...this.filterPayload,
          page: this.currentPage,
          limit: this.perPage,
          query,
        });

        this.contents = rows;
        this.appliedSearchQuery = query;
        this.totalRows = count;
      } catch (error) {
        this.contents = [];
        this.totalRows = 0;
        this.alertTopEnd('error', error?.response?.data?.message || 'Failed to load contents', false);
      } finally {
        this.isLoading = false;
      }
    },

    onChangePage() {
      this.fetchContents();
    },
  },
};
</script>
